<template>
  <div class="services-page">
    <!-- Hero Banner de Servicios -->
    <section class="services-hero">
      <div class="hero-overlay"></div>
      <v-container>
        <v-row align="center" class="py-16">
          <v-col cols="12" md="8" class="text-center text-md-left">
            <v-breadcrumbs :items="breadcrumbs" class="px-0 pb-4">
              <template slot="divider">
                <v-icon>mdi-chevron-right</v-icon>
              </template>
            </v-breadcrumbs>

            <h1 class="hero-title mb-4">
              Nuestros <span class="highlight-text">Servicios</span>
            </h1>

            <p class="hero-subtitle mb-6">
              Soluciones integrales diseñadas para transformar y potenciar tu empresa.
              Desde tecnología hasta consultoría especializada.
            </p>

            <div class="hero-stats">
              <div class="stat-item">
                <span class="stat-number">7</span>
                <span class="stat-label">Servicios Especializados</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">500+</span>
                <span class="stat-label">Clientes Satisfechos</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">10+</span>
                <span class="stat-label">Años de Experiencia</span>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="4" class="text-center">
            <v-card class="contact-card pa-6" elevation="8">
              <v-icon size="64" color="primary" class="mb-4">mdi-headset</v-icon>
              <h3 class="contact-title mb-3">¿Necesitas ayuda?</h3>
              <p class="contact-text mb-4">Consulta con nuestros especialistas</p>
              <v-btn
                color="green darken-2"
                dark
                large
                block
                @click="openWhatsAppGeneral"
                class="whatsapp-btn mb-3"
              >
                <v-icon left>mdi-whatsapp</v-icon>
                WhatsApp +51 928 180 748
              </v-btn>
              <v-btn color="primary" large block @click="scrollToContactForm" outlined>
                <v-icon left>mdi-email</v-icon>
                Formulario de Contacto
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Navegación Rápida por Servicios -->
    <section class="services-nav-section py-8">
      <v-container>
        <div class="text-center mb-8">
          <h2 class="section-title mb-4">Explora Nuestros Servicios</h2>
          <p class="section-subtitle">
            Selecciona un servicio para ver detalles específicos
          </p>
        </div>

        <v-tabs
          v-model="activeTab"
          centered
          grow
          class="services-tabs"
          background-color="transparent"
          slider-color="primary"
        >
          <v-tab
            v-for="service in services"
            :key="service.id"
            :href="`#tab-${service.id}`"
            class="service-tab"
          >
            <v-icon left>{{ service.icon }}</v-icon>
            {{ service.title }}
          </v-tab>
        </v-tabs>
      </v-container>
    </section>

    <!-- Detalles de Servicios -->
    <section class="services-details py-12">
      <v-container>
        <v-tabs-items v-model="activeTab" class="tabs-content">
          <!-- Formación Tecnológica -->
          <v-tab-item value="tab-formacion">
            <v-card class="service-detail-card pa-6" elevation="2">
              <v-row align="center">
                <v-col cols="12" md="4" class="text-center">
                  <div class="service-icon-large mb-6">
                    <v-icon size="120" color="primary">mdi-school</v-icon>
                  </div>
                  <v-btn
                    color="primary"
                    large
                    block
                    @click="openWhatsAppFormacion"
                    class="whatsapp-service-btn"
                  >
                    <v-icon left>mdi-whatsapp</v-icon>
                    Consultar Formación
                  </v-btn>
                </v-col>

                <v-col cols="12" md="8">
                  <h2 class="service-detail-title mb-4">
                    Formación Tecnológica de Alto Nivel
                  </h2>

                  <div class="service-description mb-6">
                    <p class="mb-3">
                      Ofrecemos capacitación práctica y especializada en las tecnologías
                      más demandadas del mercado actual. Nuestros programas están
                      diseñados para cerrar la brecha entre el conocimiento teórico y las
                      necesidades reales de la industria.
                    </p>

                    <v-alert type="info" border="left" class="mb-4">
                      <strong>Metodología 100% práctica:</strong> Aprendizaje basado en
                      proyectos reales
                    </v-alert>
                  </div>

                  <!-- Tecnologías Específicas -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-3">Tecnologías que Enseñamos</h3>
                    <v-row>
                      <v-col
                        cols="6"
                        sm="4"
                        md="3"
                        v-for="tech in tecnologias"
                        :key="tech.name"
                      >
                        <v-card class="tech-card pa-3 text-center" elevation="1">
                          <v-icon size="40" :color="tech.color" class="mb-2">{{
                            tech.icon
                          }}</v-icon>
                          <p class="tech-name mb-0">{{ tech.name }}</p>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Programas de Formación -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-4">Programas Especializados</h3>
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                        v-for="programa in programasFormacion"
                        :key="programa.id"
                      >
                        <v-card class="program-card pa-4" elevation="1">
                          <div class="d-flex align-center mb-3">
                            <v-avatar :color="programa.color" size="40" class="mr-3">
                              <v-icon color="white" size="20">{{ programa.icon }}</v-icon>
                            </v-avatar>
                            <h4 class="program-title mb-0">{{ programa.title }}</h4>
                          </div>
                          <p class="program-description mb-3">
                            {{ programa.description }}
                          </p>
                          <div class="program-features">
                            <div
                              v-for="feature in programa.features"
                              :key="feature"
                              class="program-feature"
                            >
                              <v-icon small color="success" class="mr-1"
                                >mdi-check</v-icon
                              >
                              <span>{{ feature }}</span>
                            </div>
                          </div>
                          <v-btn
                            :color="programa.color"
                            small
                            @click="openWhatsAppFormacionEspecifica(programa.title)"
                            class="mt-3"
                          >
                            Más Información
                          </v-btn>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Beneficios -->
                  <div class="benefits-section">
                    <h3 class="subsection-title mb-4">Beneficios Clave</h3>
                    <v-row>
                      <v-col
                        cols="12"
                        sm="6"
                        md="4"
                        v-for="benefit in beneficiosFormacion"
                        :key="benefit.id"
                      >
                        <div class="benefit-item">
                          <v-icon color="primary" class="mr-2">{{ benefit.icon }}</v-icon>
                          <div>
                            <h5 class="benefit-title">{{ benefit.title }}</h5>
                            <p class="benefit-text">{{ benefit.description }}</p>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>

          <!-- Outsourcing -->
          <v-tab-item value="tab-outsourcing">
            <v-card class="service-detail-card pa-6" elevation="2">
              <v-row align="center">
                <v-col cols="12" md="4" class="text-center">
                  <div class="service-icon-large mb-6">
                    <v-icon size="120" color="accent">mdi-account-multiple-plus</v-icon>
                  </div>
                  <v-btn
                    color="accent"
                    dark
                    large
                    block
                    @click="openWhatsAppOutsourcing"
                    class="whatsapp-service-btn"
                  >
                    <v-icon left>mdi-whatsapp</v-icon>
                    Consultar Outsourcing
                  </v-btn>
                </v-col>

                <v-col cols="12" md="8">
                  <h2 class="service-detail-title mb-4">
                    Outsourcing de Talento Especializado
                  </h2>

                  <div class="service-description mb-6">
                    <p class="mb-3">
                      Proporcionamos profesionales altamente calificados en diversas áreas
                      para integrarse a tu equipo de trabajo. Ofrecemos flexibilidad,
                      calidad garantizada y procesos de selección rigurosos.
                    </p>
                  </div>

                  <!-- Áreas de Outsourcing -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-4">Áreas de Especialización</h3>
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                        v-for="area in areasOutsourcing"
                        :key="area.id"
                      >
                        <v-card class="area-card pa-4" elevation="1">
                          <div class="d-flex align-center mb-3">
                            <v-avatar :color="area.color" size="40" class="mr-3">
                              <v-icon color="white" size="20">{{ area.icon }}</v-icon>
                            </v-avatar>
                            <div>
                              <h4 class="area-title mb-1">{{ area.title }}</h4>
                              <p class="area-subtitle mb-0">{{ area.subtitle }}</p>
                            </div>
                          </div>
                          <p class="area-description mb-3">{{ area.description }}</p>
                          <div class="area-skills">
                            <v-chip
                              v-for="skill in area.skills"
                              :key="skill"
                              small
                              class="mr-1 mb-1"
                            >
                              {{ skill }}
                            </v-chip>
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Proceso de Outsourcing -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-4">Nuestro Proceso</h3>
                    <v-timeline align-top dense>
                      <v-timeline-item
                        v-for="(step, index) in procesoOutsourcing"
                        :key="index"
                        :color="step.color"
                        small
                      >
                        <template v-slot:opposite>
                          <span class="timeline-step">{{ step.step }}</span>
                        </template>
                        <div class="timeline-content">
                          <h5 class="timeline-title">{{ step.title }}</h5>
                          <p class="timeline-description">{{ step.description }}</p>
                          <span class="timeline-time">{{ step.time }}</span>
                        </div>
                      </v-timeline-item>
                    </v-timeline>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>

          <!-- Desarrollo de Software -->
          <v-tab-item value="tab-desarrollo">
            <v-card class="service-detail-card pa-6" elevation="2">
              <v-row align="center">
                <v-col cols="12" md="4" class="text-center">
                  <div class="service-icon-large mb-6">
                    <v-icon size="120" color="success">mdi-code-braces</v-icon>
                  </div>
                  <v-btn
                    color="success"
                    dark
                    large
                    block
                    @click="openWhatsAppDesarrollo"
                    class="whatsapp-service-btn"
                  >
                    <v-icon left>mdi-whatsapp</v-icon>
                    Cotizar Desarrollo
                  </v-btn>
                </v-col>

                <v-col cols="12" md="8">
                  <h2 class="service-detail-title mb-4">
                    Desarrollo de Software a la Medida
                  </h2>

                  <div class="service-description mb-6">
                    <p class="mb-3">
                      Creamos soluciones tecnológicas innovadoras, escalables y robustas
                      adaptadas a las necesidades específicas de tu negocio. Trabajamos
                      con metodologías ágiles para garantizar resultados de calidad.
                    </p>
                  </div>

                  <!-- Tipos de Desarrollo -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-4">Tipos de Desarrollo</h3>
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                        v-for="tipo in tiposDesarrollo"
                        :key="tipo.id"
                      >
                        <v-card class="tipo-card pa-4" elevation="1">
                          <div class="d-flex align-center mb-3">
                            <v-avatar :color="tipo.color" size="40" class="mr-3">
                              <v-icon color="white" size="20">{{ tipo.icon }}</v-icon>
                            </v-avatar>
                            <h4 class="tipo-title mb-0">{{ tipo.title }}</h4>
                          </div>
                          <p class="tipo-description mb-3">{{ tipo.description }}</p>
                          <div class="tipo-technologies">
                            <v-chip
                              v-for="tech in tipo.technologies"
                              :key="tech"
                              small
                              :color="tipo.color"
                              dark
                              class="mr-1 mb-1"
                            >
                              {{ tech }}
                            </v-chip>
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Casos de Éxito -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-4">Casos de Éxito</h3>
                    <v-carousel
                      height="300"
                      hide-delimiter-background
                      class="success-carousel"
                    >
                      <v-carousel-item v-for="(caso, index) in casosExito" :key="index">
                        <v-card
                          class="caso-card pa-6"
                          height="100%"
                          :color="caso.color"
                          dark
                        >
                          <div class="d-flex align-center mb-4">
                            <v-avatar size="60" class="mr-4">
                              <v-icon size="30">{{ caso.icon }}</v-icon>
                            </v-avatar>
                            <div>
                              <h4 class="caso-title mb-1">{{ caso.title }}</h4>
                              <p class="caso-subtitle mb-0">{{ caso.industry }}</p>
                            </div>
                          </div>
                          <p class="caso-description mb-4">{{ caso.description }}</p>
                          <div class="caso-results">
                            <div
                              v-for="result in caso.results"
                              :key="result"
                              class="result-item"
                            >
                              <v-icon small class="mr-2">mdi-check-circle</v-icon>
                              <span>{{ result }}</span>
                            </div>
                          </div>
                        </v-card>
                      </v-carousel-item>
                    </v-carousel>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>

          <!-- Soporte Técnico -->
          <v-tab-item value="tab-soporte">
            <v-card class="service-detail-card pa-6" elevation="2">
              <v-row align="center">
                <v-col cols="12" md="4" class="text-center">
                  <div class="service-icon-large mb-6">
                    <v-icon size="120" color="warning">mdi-headset</v-icon>
                  </div>
                  <div class="action-buttons">
                    <v-btn
                      color="warning"
                      dark
                      large
                      block
                      @click="openWhatsAppSoporte"
                      class="whatsapp-service-btn mb-3"
                    >
                      <v-icon left>mdi-whatsapp</v-icon>
                      Soporte General
                    </v-btn>
                    <v-btn
                      color="error"
                      dark
                      large
                      block
                      @click="openEmergencyModal"
                      class="emergency-btn"
                    >
                      <v-icon left>mdi-alert-circle</v-icon>
                      EMERGENCIA 24/7
                    </v-btn>
                  </div>
                </v-col>

                <v-col cols="12" md="8">
                  <h2 class="service-detail-title mb-4">Soporte Técnico Especializado</h2>

                  <div class="service-description mb-6">
                    <p class="mb-3">
                      Ofrecemos soporte técnico integral para garantizar la continuidad
                      operativa de tus sistemas. Disponibilidad 24/7/365 con respuesta
                      inmediata y soluciones efectivas.
                    </p>
                  </div>

                  <!-- Planes de Soporte -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-4">Planes de Soporte</h3>
                    <v-row>
                      <v-col
                        cols="12"
                        md="4"
                        v-for="plan in planesSoporte"
                        :key="plan.id"
                      >
                        <v-card
                          class="plan-card pa-4"
                          :class="{ 'featured-plan': plan.featured }"
                          elevation="plan.featured ? 6 : 2"
                        >
                          <div class="plan-header mb-4">
                            <h4 class="plan-title">{{ plan.title }}</h4>
                            <div class="plan-price">
                              <span class="price-amount">{{ plan.price }}</span>
                              <span class="price-period">{{ plan.period }}</span>
                            </div>
                            <v-chip
                              v-if="plan.featured"
                              color="primary"
                              small
                              dark
                              class="featured-chip"
                            >
                              Recomendado
                            </v-chip>
                          </div>

                          <div class="plan-features mb-4">
                            <div
                              v-for="feature in plan.features"
                              :key="feature"
                              class="plan-feature"
                            >
                              <v-icon size="16" :color="plan.color" class="mr-2"
                                >mdi-check</v-icon
                              >
                              <span>{{ feature }}</span>
                            </div>
                          </div>

                          <v-btn
                            :color="plan.color"
                            dark
                            block
                            @click="openWhatsAppSoportePlan(plan.title)"
                          >
                            Contratar Plan
                          </v-btn>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Niveles de Soporte -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-4">Niveles de Soporte</h3>
                    <v-data-table
                      :headers="nivelesHeaders"
                      :items="nivelesSoporte"
                      hide-default-footer
                      class="soporte-table"
                    >
                      <template slot="item.color" slot-scope="{ item }">
                        <v-chip :color="item.color" small dark>{{ item.level }}</v-chip>
                      </template>
                      <template slot="item.response" slot-scope="{ item }">
                        <v-icon :color="item.responseColor" class="mr-1"
                          >mdi-clock-outline</v-icon
                        >
                        <span>{{ item.response }}</span>
                      </template>
                      <template slot="item.coverage" slot-scope="{ item }">
                        <div class="coverage-item">
                          <v-icon
                            v-for="n in 5"
                            :key="n"
                            size="16"
                            :color="n <= item.coverage ? 'primary' : 'grey lighten-2'"
                          >
                            mdi-checkbox-blank-circle
                          </v-icon>
                        </div>
                      </template>
                    </v-data-table>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>

          <!-- Asesoría Contable -->
          <v-tab-item value="tab-contable">
            <v-card class="service-detail-card pa-6" elevation="2">
              <v-row align="center">
                <v-col cols="12" md="4" class="text-center">
                  <div class="service-icon-large mb-6">
                    <v-icon size="120" color="purple">mdi-calculator</v-icon>
                  </div>
                  <v-btn
                    color="purple"
                    dark
                    large
                    block
                    @click="openWhatsAppContable"
                    class="whatsapp-service-btn"
                  >
                    <v-icon left>mdi-whatsapp</v-icon>
                    Asesoría Contable
                  </v-btn>
                </v-col>

                <v-col cols="12" md="8">
                  <h2 class="service-detail-title mb-4">
                    Asesoría Contable & Fiscal Integral
                  </h2>

                  <div class="service-description mb-6">
                    <p class="mb-3">
                      Brindamos servicios contables completos que aseguran el cumplimiento
                      normativo y optimizan la gestión financiera de tu empresa.
                    </p>
                  </div>

                  <!-- Servicios Contables -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-4">Nuestros Servicios Contables</h3>
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                        v-for="servicio in serviciosContables"
                        :key="servicio.id"
                      >
                        <v-card class="servicio-card pa-4" elevation="1">
                          <div class="d-flex align-center mb-3">
                            <v-avatar :color="servicio.color" size="40" class="mr-3">
                              <v-icon color="white" size="20">{{ servicio.icon }}</v-icon>
                            </v-avatar>
                            <h4 class="servicio-title mb-0">{{ servicio.title }}</h4>
                          </div>
                          <p class="servicio-description mb-3">
                            {{ servicio.description }}
                          </p>
                          <div class="servicio-features">
                            <v-chip
                              v-for="feature in servicio.features"
                              :key="feature"
                              small
                              :color="servicio.color"
                              outlined
                              class="mr-1 mb-1"
                            >
                              {{ feature }}
                            </v-chip>
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>

          <!-- Recursos Humanos -->
          <v-tab-item value="tab-rrhh">
            <v-card class="service-detail-card pa-6" elevation="2">
              <v-row align="center">
                <v-col cols="12" md="4" class="text-center">
                  <div class="service-icon-large mb-6">
                    <v-icon size="120" color="teal">mdi-account-tie</v-icon>
                  </div>
                  <v-btn
                    color="teal"
                    dark
                    large
                    block
                    @click="openWhatsAppRRHH"
                    class="whatsapp-service-btn"
                  >
                    <v-icon left>mdi-whatsapp</v-icon>
                    Consultoría RRHH
                  </v-btn>
                </v-col>

                <v-col cols="12" md="8">
                  <h2 class="service-detail-title mb-4">
                    Consultoría en Recursos Humanos
                  </h2>

                  <div class="service-description mb-6">
                    <p class="mb-3">
                      Gestionamos integralmente el talento humano de tu organización para
                      maximizar el potencial y mejorar el clima laboral.
                    </p>
                  </div>

                  <!-- Servicios RRHH -->
                  <div class="mb-6">
                    <h3 class="subsection-title mb-4">Servicios de Recursos Humanos</h3>
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                        v-for="servicio in serviciosRRHH"
                        :key="servicio.id"
                      >
                        <v-card class="servicio-rrhh-card pa-4" elevation="1">
                          <div class="d-flex align-center mb-3">
                            <v-avatar :color="servicio.color" size="40" class="mr-3">
                              <v-icon color="white" size="20">{{ servicio.icon }}</v-icon>
                            </v-avatar>
                            <h4 class="servicio-title mb-0">{{ servicio.title }}</h4>
                          </div>
                          <p class="servicio-description mb-3">
                            {{ servicio.description }}
                          </p>
                          <div class="servicio-process">
                            <v-progress-linear
                              :value="servicio.process"
                              height="8"
                              :color="servicio.color"
                              rounded
                              class="mb-2"
                            ></v-progress-linear>
                            <div class="d-flex justify-space-between">
                              <span class="process-label">Proceso</span>
                              <span class="process-value">{{ servicio.process }}%</span>
                            </div>
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-container>
    </section>

    <!-- Comparativa de Servicios -->
    <section class="comparison-section py-12">
      <v-container>
        <div class="text-center mb-8">
          <h2 class="section-title mb-4">Comparativa de Servicios</h2>
          <p class="section-subtitle">Encuentra el servicio ideal para tus necesidades</p>
        </div>

        <v-card class="comparison-card pa-6" elevation="4">
          <v-data-table
            :headers="comparisonHeaders"
            :items="comparisonData"
            hide-default-footer
            class="comparison-table"
          >
            <template slot="item.service" slot-scope="{ item }">
              <div class="d-flex align-center">
                <v-avatar :color="item.color" size="36" class="mr-3">
                  <v-icon color="white" size="18">{{ item.icon }}</v-icon>
                </v-avatar>
                <strong>{{ item.service }}</strong>
              </div>
            </template>

            <template slot="item.duration" slot-scope="{ item }">
              <v-chip small :color="item.durationColor">{{ item.duration }}</v-chip>
            </template>

            <template slot="item.investment" slot-scope="{ item }">
              <span class="investment-amount">{{ item.investment }}</span>
            </template>

            <template slot="item.recommended" slot-scope="{ item }">
              <v-icon v-if="item.recommended" color="success">mdi-check-circle</v-icon>
              <v-icon v-else color="grey lighten-1">mdi-close-circle</v-icon>
            </template>

            <template slot="item.action" slot-scope="{ item }">
              <v-btn small :color="item.color" dark @click="openWhatsAppService(item.id)">
                <v-icon left small>mdi-whatsapp</v-icon>
                Consultar
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-container>
    </section>

    <!-- Formulario de Contacto -->
    <section class="contact-section py-12" id="contact-form">
      <v-container>
        <div class="text-center mb-8">
          <h2 class="section-title mb-4">¿Listo para Transformar tu Empresa?</h2>
          <p class="section-subtitle">
            Completa el formulario y nos pondremos en contacto contigo
          </p>
        </div>

        <v-row justify="center">
          <v-col cols="12" lg="10">
            <v-card class="contact-form-card pa-6" elevation="4">
              <v-row>
                <v-col cols="12" md="7">
                  <v-form
                    ref="contactForm"
                    v-model="formValid"
                    @submit.prevent="submitForm"
                  >
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.nombre"
                          label="Nombre completo *"
                          :rules="[(v) => !!v || 'El nombre es requerido']"
                          outlined
                          required
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.empresa"
                          label="Empresa"
                          outlined
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.email"
                          label="Email *"
                          type="email"
                          :rules="[
                            (v) => !!v || 'El email es requerido',
                            (v) => /.+@.+\..+/.test(v) || 'Email debe ser válido',
                          ]"
                          outlined
                          required
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.telefono"
                          label="Teléfono *"
                          :rules="[(v) => !!v || 'El teléfono es requerido']"
                          outlined
                          required
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12">
                        <v-select
                          v-model="form.servicio"
                          :items="services.map((s) => s.title)"
                          label="Servicio de interés *"
                          :rules="[(v) => !!v || 'Selecciona un servicio']"
                          outlined
                          required
                        ></v-select>
                      </v-col>

                      <v-col cols="12">
                        <v-textarea
                          v-model="form.mensaje"
                          label="Mensaje *"
                          :rules="[(v) => !!v || 'El mensaje es requerido']"
                          outlined
                          rows="4"
                          required
                        ></v-textarea>
                      </v-col>

                      <v-col cols="12">
                        <v-checkbox
                          v-model="form.privacidad"
                          :rules="[
                            (v) => !!v || 'Debes aceptar la política de privacidad',
                          ]"
                          required
                        >
                          <template v-slot:label>
                            <span
                              >He leído y acepto la
                              <a href="#" @click.prevent="openPrivacy"
                                >política de privacidad</a
                              ></span
                            >
                          </template>
                        </v-checkbox>
                      </v-col>

                      <v-col cols="12" class="text-center">
                        <v-btn
                          color="primary"
                          x-large
                          type="submit"
                          :loading="formLoading"
                          :disabled="!formValid || formLoading"
                          class="submit-btn"
                          depressed
                        >
                          <v-icon left>mdi-send</v-icon>
                          Enviar Consulta
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-col>

                <v-col cols="12" md="5">
                  <div class="contact-info-sidebar">
                    <h3 class="sidebar-title mb-4">Otras Formas de Contacto</h3>

                    <div class="contact-method mb-6">
                      <v-icon color="green darken-2" large class="mr-3"
                        >mdi-whatsapp</v-icon
                      >
                      <div>
                        <h4 class="method-title">WhatsApp</h4>
                        <p class="method-detail">+51 928 180 748</p>
                        <v-btn
                          color="green darken-2"
                          dark
                          small
                          @click="openWhatsAppGeneral"
                        >
                          <v-icon left small>mdi-whatsapp</v-icon>
                          Enviar Mensaje
                        </v-btn>
                      </div>
                    </div>

                    <div class="contact-method mb-6">
                      <v-icon color="primary" large class="mr-3">mdi-email</v-icon>
                      <div>
                        <h4 class="method-title">Email</h4>
                        <p class="method-detail">informes@consultora-innova.com</p>
                      </div>
                    </div>

                    <div class="contact-method mb-6">
                      <v-icon color="accent" large class="mr-3">mdi-phone</v-icon>
                      <div>
                        <h4 class="method-title">Teléfono</h4>
                        <p class="method-detail">+1 (555) 123-4567</p>
                      </div>
                    </div>

                    <div class="contact-hours">
                      <h4 class="hours-title mb-2">Horarios de Atención</h4>
                      <p class="hours-detail">Lunes a Viernes: 9:00 - 18:00</p>
                      <p class="hours-detail">Sábados: 9:00 - 13:00</p>
                      <p class="hours-detail emergency-hours">
                        <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                        Soporte Emergencia: 24/7
                      </p>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Botón WhatsApp Flotante -->
    <v-btn
      color="green darken-2"
      dark
      fab
      large
      class="whatsapp-fab"
      @click="openWhatsAppGeneral"
      fixed
      bottom
      right
    >
      <v-icon size="28">mdi-whatsapp</v-icon>
    </v-btn>

    <!-- Modal de Emergencia -->
    <EmergencyModal
      :dialog="emergencyDialog"
      @close="emergencyDialog = false"
      @whatsapp="openEmergencyWhatsApp"
      @call="callEmergency"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000" top>
      <div class="d-flex align-center">
        <v-icon class="mr-3">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.message }}</span>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import EmergencyModal from "@/components/EmergencyModal.vue";

export default {
  name: "ServicesPage",
  components: {
    EmergencyModal,
  },
  data() {
    return {
      activeTab: "tab-formacion",
      emergencyDialog: false,
      formValid: false,
      formLoading: false,

      breadcrumbs: [
        { text: "Inicio", disabled: false, href: "/" },
        { text: "Servicios", disabled: true },
      ],

      menuItems: [
        { title: "Inicio", path: "/", icon: "mdi-home", color: "primary" },
        {
          title: "Servicios",
          path: "/servicios",
          icon: "mdi-briefcase",
          color: "accent",
        },
        {
          title: "Nosotros",
          path: "/nosotros",
          icon: "mdi-account-group",
          color: "warning",
        },
        {
          title: "Soluciones",
          path: "/soluciones",
          icon: "mdi-puzzle",
          color: "success",
        },
        { title: "Contacto", path: "/contacto", icon: "mdi-headset", color: "error" },
      ],

      services: [
        {
          id: "formacion",
          icon: "mdi-school",
          title: "Formación Tecnológica",
          description: "Capacitación especializada en tecnologías emergentes",
          color: "primary",
          features: [
            "Certificaciones internacionales",
            "Metodología práctica",
            "Instructores expertos",
          ],
        },
        {
          id: "outsourcing",
          icon: "mdi-account-multiple-plus",
          title: "Outsourcing",
          description: "Talento especializado para tus proyectos",
          color: "accent",
          features: ["Integración rápida", "Garantía de calidad", "Flexibilidad total"],
        },
        {
          id: "desarrollo",
          icon: "mdi-code-braces",
          title: "Desarrollo de Software",
          description: "Soluciones tecnológicas a medida",
          color: "success",
          features: ["Desarrollo ágil", "Seguridad garantizada", "Soporte continuo"],
        },
        {
          id: "soporte",
          icon: "mdi-headset",
          title: "Soporte Técnico",
          description: "Asistencia especializada 24/7",
          color: "warning",
          features: [
            "Disponibilidad 99.9%",
            "Respuesta inmediata",
            "Monitoreo proactivo",
          ],
        },
        {
          id: "contable",
          icon: "mdi-calculator",
          title: "Asesoría Contable",
          description: "Consultoría fiscal y contable integral",
          color: "purple",
          features: ["Cumplimiento normativo", "Optimización fiscal", "Auditorías"],
        },
        {
          id: "rrhh",
          icon: "mdi-account-tie",
          title: "Recursos Humanos",
          description: "Gestión integral del talento",
          color: "teal",
          features: ["Reclutamiento", "Desarrollo organizacional", "Administración"],
        },
      ],

      form: {
        nombre: "",
        empresa: "",
        email: "",
        telefono: "",
        servicio: null,
        mensaje: "",
        privacidad: false,
      },

      // Data específica para cada servicio
      tecnologias: [
        { name: "Java", icon: "mdi-language-java", color: "red" },
        { name: ".NET", icon: "mdi-microsoft", color: "purple" },
        { name: "Python", icon: "mdi-language-python", color: "blue" },
        { name: "JavaScript", icon: "mdi-language-javascript", color: "yellow darken-2" },
        { name: "React", icon: "mdi-react", color: "cyan" },
        { name: "Angular", icon: "mdi-angular", color: "red darken-1" },
        { name: "Vue.js", icon: "mdi-vuejs", color: "green darken-1" },
        { name: "Node.js", icon: "mdi-nodejs", color: "green" },
        { name: "AWS", icon: "mdi-aws", color: "orange" },
        { name: "Azure", icon: "mdi-microsoft-azure", color: "blue" },
        { name: "Docker", icon: "mdi-docker", color: "blue darken-3" },
        { name: "Kubernetes", icon: "mdi-kubernetes", color: "blue darken-4" },
      ],

      programasFormacion: [
        {
          id: 1,
          title: "Desarrollador Full Stack",
          description: "Formación completa en desarrollo web frontend y backend",
          color: "primary",
          icon: "mdi-code-braces",
          features: [
            "HTML5/CSS3/JavaScript",
            "React/Vue.js",
            "Node.js/Express",
            "Bases de datos",
            "Git/GitHub",
          ],
        },
        {
          id: 2,
          title: "Data Science & AI",
          description: "Especialización en ciencia de datos e inteligencia artificial",
          color: "purple",
          icon: "mdi-chart-bell-curve",
          features: [
            "Python avanzado",
            "Machine Learning",
            "Deep Learning",
            "Análisis estadístico",
            "Visualización de datos",
          ],
        },
        {
          id: 3,
          title: "DevOps & Cloud",
          description: "Implementación de infraestructura y despliegue en la nube",
          color: "blue",
          icon: "mdi-cloud",
          features: [
            "AWS/Azure/GCP",
            "Docker/Kubernetes",
            "CI/CD",
            "Infraestructura como código",
            "Monitoreo",
          ],
        },
        {
          id: 4,
          title: "Ciberseguridad",
          description: "Protección de sistemas y datos contra amenazas digitales",
          color: "red",
          icon: "mdi-shield-check",
          features: [
            "Pentesting",
            "Análisis forense",
            "Seguridad web",
            "Criptografía",
            "Compliance",
          ],
        },
      ],

      beneficiosFormacion: [
        {
          id: 1,
          icon: "mdi-certificate",
          title: "Certificación Internacional",
          description: "Certificados reconocidos a nivel mundial",
        },
        {
          id: 2,
          icon: "mdi-briefcase-check",
          title: "Bolsa de Trabajo",
          description: "Acceso a oportunidades laborales exclusivas",
        },
        {
          id: 3,
          icon: "mdi-account-group",
          title: "Mentoría Personalizada",
          description: "Seguimiento individualizado por expertos",
        },
        {
          id: 4,
          icon: "mdi-update",
          title: "Actualización Constante",
          description: "Contenido siempre actualizado con tendencias",
        },
        {
          id: 5,
          icon: "mdi-handshake",
          title: "Networking",
          description: "Conexión con profesionales de la industria",
        },
        {
          id: 6,
          icon: "mdi-finance",
          title: "Financiamiento Flexible",
          description: "Opciones de pago adaptadas a tus necesidades",
        },
      ],

      areasOutsourcing: [
        {
          id: 1,
          title: "Desarrollo de Software",
          subtitle: "Programadores y arquitectos",
          description:
            "Profesionales especializados en diversas tecnologías y frameworks",
          color: "success",
          icon: "mdi-code-braces",
          skills: ["Java", ".NET", "Python", "JavaScript", "React", "Angular", "Vue.js"],
        },
        {
          id: 2,
          title: "Infraestructura IT",
          subtitle: "Sysadmins y DevOps",
          description: "Expertos en administración de sistemas y despliegue en la nube",
          color: "blue",
          icon: "mdi-server",
          skills: [
            "Linux",
            "Windows Server",
            "AWS",
            "Azure",
            "Docker",
            "Kubernetes",
            "CI/CD",
          ],
        },
        {
          id: 3,
          title: "Ciberseguridad",
          subtitle: "Analistas y pentesters",
          description:
            "Especialistas en protección de sistemas y análisis de vulnerabilidades",
          color: "red",
          icon: "mdi-shield-lock",
          skills: [
            "Pentesting",
            "SIEM",
            "Firewalls",
            "IDS/IPS",
            "Forensics",
            "Compliance",
          ],
        },
        {
          id: 4,
          title: "Contabilidad y Finanzas",
          subtitle: "Contadores y analistas",
          description: "Profesionales con experiencia en gestión financiera y fiscal",
          color: "purple",
          icon: "mdi-calculator",
          skills: [
            "Contabilidad",
            "Impuestos",
            "Auditoría",
            "Nóminas",
            "Reportes financieros",
          ],
        },
      ],

      procesoOutsourcing: [
        {
          step: "01",
          title: "Análisis de Necesidades",
          description:
            "Evaluamos detalladamente los requerimientos específicos del proyecto",
          time: "1-2 días",
          color: "primary",
        },
        {
          step: "02",
          title: "Selección de Talentos",
          description: "Buscamos y filtramos candidatos según el perfil requerido",
          time: "3-5 días",
          color: "accent",
        },
        {
          step: "03",
          title: "Entrevistas y Evaluación",
          description: "Realizamos entrevistas técnicas y pruebas de habilidades",
          time: "2-3 días",
          color: "success",
        },
        {
          step: "04",
          title: "Integración del Equipo",
          description: "Onboarding y adaptación al entorno de trabajo del cliente",
          time: "1-2 días",
          color: "warning",
        },
        {
          step: "05",
          title: "Seguimiento y Soporte",
          description: "Acompañamiento continuo y evaluación de desempeño",
          time: "Continuo",
          color: "error",
        },
      ],

      tiposDesarrollo: [
        {
          id: 1,
          title: "Aplicaciones Web",
          description:
            "Desarrollo de plataformas y sistemas web responsivos y escalables",
          color: "blue",
          icon: "mdi-web",
          technologies: ["React", "Vue.js", "Angular", "Node.js", "Python", "Java"],
        },
        {
          id: 2,
          title: "Aplicaciones Móviles",
          description: "Creación de apps nativas e híbridas para iOS y Android",
          color: "purple",
          icon: "mdi-cellphone",
          technologies: ["React Native", "Flutter", "Swift", "Kotlin", "Ionic"],
        },
        {
          id: 3,
          title: "Sistemas Empresariales",
          description: "Desarrollo de software corporativo y sistemas de gestión",
          color: "green",
          icon: "mdi-office-building",
          technologies: [".NET", "Java EE", "Spring", "ERP", "CRM"],
        },
        {
          id: 4,
          title: "E-commerce",
          description: "Plataformas de venta online y sistemas de pago",
          color: "orange",
          icon: "mdi-cart",
          technologies: ["Magento", "Shopify", "WooCommerce", "Stripe", "PayPal"],
        },
      ],

      casosExito: [
        {
          title: "Sistema de Gestión Hotelera",
          industry: "Hospitalidad",
          description:
            "Desarrollo de un sistema integral de reservas, check-in y gestión de habitaciones",
          color: "primary",
          icon: "mdi-hotel",
          results: [
            "Reducción del 40% en procesos manuales",
            "Incremento del 25% en ocupación",
            "Mejora del 60% en experiencia del cliente",
          ],
        },
        {
          title: "Plataforma Educativa",
          industry: "Educación",
          description:
            "Creación de una plataforma de e-learning con más de 10,000 usuarios",
          color: "success",
          icon: "mdi-school",
          results: [
            "50,000+ horas de contenido",
            "95% de satisfacción de usuarios",
            "Escalabilidad a nivel nacional",
          ],
        },
        {
          title: "Sistema de Logística",
          industry: "Transporte",
          description: "Desarrollo de software de seguimiento y optimización de rutas",
          color: "warning",
          icon: "mdi-truck",
          results: [
            "Reducción del 30% en costos de combustible",
            "Optimización del 45% en rutas",
            "Monitoreo en tiempo real",
          ],
        },
      ],

      planesSoporte: [
        {
          id: 1,
          title: "Básico",
          price: "$299",
          period: "/mes",
          color: "grey",
          icon: "mdi-headset",
          features: [
            "Soporte 8x5 (Lun-Vie)",
            "Respuesta en 24 horas",
            "Acceso remoto básico",
            "Actualizaciones de seguridad",
            "Informes mensuales",
          ],
          featured: false,
        },
        {
          id: 2,
          title: "Profesional",
          price: "$699",
          period: "/mes",
          color: "primary",
          icon: "mdi-headset",
          features: [
            "Soporte 24x7",
            "Respuesta en 4 horas",
            "Acceso remoto completo",
            "Monitoreo proactivo",
            "Informes semanales",
            "Capacitación básica",
          ],
          featured: true,
        },
        {
          id: 3,
          title: "Enterprise",
          price: "$1,299",
          period: "/mes",
          color: "success",
          icon: "mdi-headset",
          features: [
            "Soporte 24x7 prioritario",
            "Respuesta en 1 hora",
            "Acceso remoto avanzado",
            "Monitoreo 24/7",
            "Informes diarios",
            "Capacitación personalizada",
            "Gerente de cuenta dedicado",
          ],
          featured: false,
        },
      ],

      nivelesHeaders: [
        { text: "Nivel", value: "color" },
        { text: "Servicio", value: "service" },
        { text: "Tiempo de Respuesta", value: "response" },
        { text: "Cobertura", value: "coverage" },
        { text: "SLA", value: "sla" },
      ],

      nivelesSoporte: [
        {
          level: "L1",
          service: "Soporte Básico",
          response: "24 horas",
          responseColor: "green",
          coverage: 3,
          sla: "99.5%",
          color: "grey",
        },
        {
          level: "L2",
          service: "Soporte Técnico",
          response: "8 horas",
          responseColor: "blue",
          coverage: 4,
          sla: "99.8%",
          color: "primary",
        },
        {
          level: "L3",
          service: "Soporte Especializado",
          response: "4 horas",
          responseColor: "orange",
          coverage: 5,
          sla: "99.9%",
          color: "warning",
        },
        {
          level: "L4",
          service: "Soporte Crítico",
          response: "1 hora",
          responseColor: "red",
          coverage: 5,
          sla: "99.95%",
          color: "error",
        },
      ],

      serviciosContables: [
        {
          id: 1,
          title: "Contabilidad General",
          description:
            "Registro y organización de todas las operaciones contables de la empresa",
          color: "blue",
          icon: "mdi-book-open",
          features: [
            "Libros contables",
            "Balances",
            "Estados financieros",
            "Conciliaciones",
          ],
        },
        {
          id: 2,
          title: "Declaraciones Impositivas",
          description:
            "Preparación y presentación de impuestos ante SUNAT y otros organismos",
          color: "purple",
          icon: "mdi-file-document",
          features: ["IGV", "Renta", "Detracciones", "Retenciones"],
        },
        {
          id: 3,
          title: "Nóminas y Planillas",
          description: "Gestión completa de remuneraciones y beneficios laborales",
          color: "green",
          icon: "mdi-account-cash",
          features: ["Boletas de pago", "CTS", "Gratificaciones", "Vacaciones"],
        },
        {
          id: 4,
          title: "Auditoría Externa",
          description:
            "Revisión independiente de estados financieros y procesos contables",
          color: "orange",
          icon: "mdi-magnify",
          features: [
            "Auditoría financiera",
            "Auditoría operativa",
            "Informes",
            "Recomendaciones",
          ],
        },
      ],

      serviciosRRHH: [
        {
          id: 1,
          title: "Reclutamiento y Selección",
          description:
            "Proceso completo de búsqueda, evaluación y contratación de talento",
          color: "teal",
          icon: "mdi-account-search",
          process: 85,
        },
        {
          id: 2,
          title: "Capacitación y Desarrollo",
          description: "Diseño e implementación de programas de formación para empleados",
          color: "blue",
          icon: "mdi-school",
          process: 75,
        },
        {
          id: 3,
          title: "Evaluación de Desempeño",
          description: "Sistemas de medición y mejora del rendimiento laboral",
          color: "purple",
          icon: "mdi-chart-line",
          process: 90,
        },
        {
          id: 4,
          title: "Clima Organizacional",
          description: "Diagnóstico y mejora del ambiente laboral y satisfacción",
          color: "green",
          icon: "mdi-account-group",
          process: 80,
        },
      ],

      comparisonHeaders: [
        { text: "Servicio", value: "service" },
        { text: "Duración", value: "duration" },
        { text: "Inversión", value: "investment" },
        { text: "Certificación", value: "certified" },
        { text: "Soporte Incluido", value: "support" },
        { text: "Recomendado para", value: "recommended" },
        { text: "", value: "action" },
      ],

      comparisonData: [
        {
          id: "formacion",
          service: "Formación Tecnológica",
          icon: "mdi-school",
          color: "primary",
          duration: "2-6 meses",
          durationColor: "blue",
          investment: "Desde $500",
          certified: true,
          support: "6 meses",
          recommended: "Desarrolladores y técnicos",
          recommendedFor: true,
        },
        {
          id: "outsourcing",
          service: "Outsourcing",
          icon: "mdi-account-multiple-plus",
          color: "accent",
          duration: "Flexible",
          durationColor: "green",
          investment: "Desde $1,500/mes",
          certified: true,
          support: "Incluido",
          recommended: "Proyectos específicos",
          recommendedFor: true,
        },
        {
          id: "desarrollo",
          service: "Desarrollo Software",
          icon: "mdi-code-braces",
          color: "success",
          duration: "3-12 meses",
          durationColor: "orange",
          investment: "Desde $5,000",
          certified: true,
          support: "12 meses",
          recommended: "Empresas en transformación",
          recommendedFor: true,
        },
        {
          id: "soporte",
          service: "Soporte Técnico",
          icon: "mdi-headset",
          color: "warning",
          duration: "Continuo",
          durationColor: "purple",
          investment: "Desde $299/mes",
          certified: true,
          support: "24/7",
          recommended: "Todas las empresas",
          recommendedFor: true,
        },
      ],

      snackbar: {
        show: false,
        message: "",
        color: "",
        icon: "",
      },

      whatsappNumber: "51917915824",
    };
  },
  methods: {
    // Métodos de WhatsApp
    openWhatsApp(message) {
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${this.whatsappNumber}?text=${encodedMessage}`;
      window.open(whatsappUrl, "_blank");
    },

    openWhatsAppGeneral() {
      const message = `Hola, soy [Tu Nombre]. Necesito información sobre los servicios de Consultora Innova A&G:

• Empresa/Persona: 
• Servicio de interés: 
• Necesidad específica: 

Por favor contáctenme para coordinar una reunión.`;
      this.openWhatsApp(message);
    },

    openWhatsAppFormacion() {
      const message = `Hola, necesito información sobre formación tecnológica:

• Área de interés: 
• Nivel: (Básico/Intermedio/Avanzado)
• Cantidad de participantes: 
• Modalidad preferida: (Presencial/Virtual)

Gracias.`;
      this.openWhatsApp(message);
    },

    openWhatsAppFormacionEspecifica(programa) {
      const message = `Hola, necesito información específica sobre el programa: ${programa}

• Nombre: 
• Correo electrónico: 
• Nivel actual de conocimiento: 
• Disponibilidad horaria: 

Gracias.`;
      this.openWhatsApp(message);
    },

    openWhatsAppOutsourcing() {
      const message = `Hola, necesito información sobre outsourcing de talento:

• Perfil requerido: 
• Cantidad de profesionales: 
• Duración del proyecto: 
• Habilidades específicas: 

Por favor contáctenme.`;
      this.openWhatsApp(message);
    },

    openWhatsAppDesarrollo() {
      const message = `Hola, necesito cotización para desarrollo de software:

• Tipo de proyecto: (Web/Móvil/Sistema)
• Funcionalidades principales: 
• Plazo estimado: 
• Presupuesto aproximado: 

Solicito reunión de análisis.`;
      this.openWhatsApp(message);
    },

    openWhatsAppSoporte() {
      const message = `Solicito soporte técnico:

• Sistema/plataforma: 
• Descripción del problema: 
• Nivel de urgencia: (Alta/Media/Baja)
• Contacto inmediato: 

Necesito asistencia.`;
      this.openWhatsApp(message);
    },

    openWhatsAppSoportePlan(plan) {
      const message = `Hola, quiero contratar el plan de soporte: ${plan}

• Empresa: 
• Cantidad de usuarios: 
• Sistemas a soportar: 
• Fecha de inicio deseada: 

Por favor envíenme la propuesta.`;
      this.openWhatsApp(message);
    },

    openWhatsAppContable() {
      const message = `Hola, necesito asesoría contable:

• Tipo de empresa: 
• Servicio requerido: (Declaraciones/Auditoría/Nóminas)
• Plazo: 
• Información adicional: 

Por favor contáctenme.`;
      this.openWhatsApp(message);
    },

    openWhatsAppRRHH() {
      const message = `Hola, necesito consultoría en RRHH:

• Servicio requerido: (Reclutamiento/Capacitación/Desarrollo)
• Cantidad de colaboradores: 
• Necesidad específica: 
• Plazo: 

Gracias.`;
      this.openWhatsApp(message);
    },

    openWhatsAppService(serviceId) {
      const service = this.services.find((s) => s.id === serviceId);
      if (service) {
        switch (serviceId) {
          case "formacion":
            this.openWhatsAppFormacion();
            break;
          case "outsourcing":
            this.openWhatsAppOutsourcing();
            break;
          case "desarrollo":
            this.openWhatsAppDesarrollo();
            break;
          case "soporte":
            this.openWhatsAppSoporte();
            break;
          case "contable":
            this.openWhatsAppContable();
            break;
          case "rrhh":
            this.openWhatsAppRRHH();
            break;
          default:
            this.openWhatsAppGeneral();
        }
      }
    },

    // Métodos de emergencia
    openEmergencyModal() {
      this.emergencyDialog = true;
    },

    openEmergencyWhatsApp() {
      const message = `EMERGENCIA TÉCNICA - URGENTE:

• Sistema afectado: 
• Descripción de la emergencia: 
• Impacto en operaciones: 
• Contacto para coordinación: 

NECESITO ASISTENCIA INMEDIATA.`;
      this.openWhatsApp(message);
      this.emergencyDialog = false;
    },

    callEmergency() {
      window.open(`tel:+51${this.whatsappNumber}`, "_self");
      this.emergencyDialog = false;
    },

    // Navegación
    scrollToContactForm() {
      const element = document.getElementById("contact-form");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },

    // Formulario
    async submitForm() {
      if (this.$refs.contactForm.validate()) {
        this.formLoading = true;

        try {
          // Simular envío de formulario
          await new Promise((resolve) => setTimeout(resolve, 1500));

          this.showSnackbar(
            "¡Consulta enviada con éxito! Te contactaremos en menos de 24 horas.",
            "success",
            "mdi-check-circle"
          );

          // Resetear formulario
          this.$refs.contactForm.reset();
          this.form.privacidad = false;
        } catch (error) {
          this.showSnackbar(
            "Error al enviar la consulta. Por favor, inténtalo de nuevo.",
            "error",
            "mdi-alert-circle"
          );
        } finally {
          this.formLoading = false;
        }
      }
    },

    openPrivacy() {
      this.showSnackbar("Política de privacidad abierta", "info", "mdi-shield-account");
    },

    // Notificaciones
    showSnackbar(message, color, icon) {
      this.snackbar = {
        show: true,
        message,
        color,
        icon,
      };
    },
  },
};
</script>

<style scoped>
.services-page {
  background: #f8fafc;
}

/* Hero Section */
.services-hero {
  background: linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
}

.hero-title {
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.2;
}

.highlight-text {
  background: linear-gradient(135deg, #25d366, #128c7e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
}

.hero-stats {
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1;
  background: linear-gradient(135deg, #25d366, #128c7e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 0.5rem;
}

.contact-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  position: relative;
  z-index: 2;
}

.contact-title {
  color: #2d3748;
  font-weight: 700;
}

.contact-text {
  color: #4a5568;
}

.whatsapp-btn {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%) !important;
  border-radius: 12px !important;
  font-weight: 600;
  transition: all 0.3s ease !important;
}

.whatsapp-btn:hover {
  transform: translateY(-3px) !important;
  box-shadow: 0 8px 25px rgba(37, 211, 102, 0.3) !important;
}

/* Services Navigation */
.services-nav-section {
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.services-tabs {
  border-bottom: 2px solid #e2e8f0;
}

.service-tab {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.3px;
  min-height: 64px;
  transition: all 0.3s ease;
}

.service-tab:hover {
  background: rgba(0, 86, 179, 0.05);
}

/* Service Details */
.service-detail-card {
  border-radius: 20px;
  border-left: 6px solid;
  transition: all 0.3s ease;
}

.service-icon-large {
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.service-detail-title {
  font-size: 2rem;
  font-weight: 800;
  color: #2d3748;
  margin-bottom: 1.5rem;
}

.whatsapp-service-btn {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%) !important;
  border-radius: 12px !important;
  font-weight: 600;
  transition: all 0.3s ease !important;
}

.whatsapp-service-btn:hover {
  transform: translateY(-3px) !important;
  box-shadow: 0 8px 25px rgba(37, 211, 102, 0.3) !important;
}

.subsection-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e2e8f0;
}

/* Tech Cards */
.tech-card {
  transition: all 0.3s ease;
  border-radius: 12px;
  cursor: pointer;
}

.tech-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.tech-name {
  font-weight: 600;
  color: #2d3748;
}

/* Program Cards */
.program-card {
  border-radius: 12px;
  border-left: 4px solid;
  transition: all 0.3s ease;
  height: 100%;
}

.program-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.program-title {
  font-weight: 700;
  color: #2d3748;
}

.program-description {
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
}

.program-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.program-feature {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #4a5568;
}

/* Benefits */
.benefit-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.benefit-title {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.benefit-text {
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Area Cards */
.area-card {
  border-radius: 12px;
  border-left: 4px solid;
  transition: all 0.3s ease;
  height: 100%;
}

.area-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.area-title {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0;
}

.area-subtitle {
  color: #718096;
  font-size: 0.85rem;
}

.area-description {
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
}

.area-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

/* Timeline */
.timeline-step {
  font-weight: 700;
  color: #2d3748;
}

.timeline-content {
  padding-left: 1rem;
}

.timeline-title {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.timeline-description {
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 0.25rem;
}

.timeline-time {
  font-size: 0.85rem;
  color: #718096;
  font-weight: 500;
}

/* Tipo Cards */
.tipo-card {
  border-radius: 12px;
  border-left: 4px solid;
  transition: all 0.3s ease;
  height: 100%;
}

.tipo-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.tipo-title {
  font-weight: 700;
  color: #2d3748;
}

.tipo-description {
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
}

.tipo-technologies {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

/* Casos de Éxito */
.success-carousel {
  border-radius: 12px;
  overflow: hidden;
}

.caso-card {
  border-radius: 12px;
  height: 100%;
}

.caso-title {
  font-weight: 700;
  font-size: 1.25rem;
}

.caso-subtitle {
  font-size: 0.9rem;
  opacity: 0.9;
}

.caso-description {
  font-size: 0.95rem;
  line-height: 1.5;
  opacity: 0.9;
}

.result-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

/* Planes de Soporte */
.plan-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  height: 100%;
  position: relative;
}

.plan-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.featured-plan {
  border: 2px solid #0056b3;
  transform: scale(1.05);
}

.plan-header {
  text-align: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.plan-title {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.plan-price {
  margin-bottom: 0.5rem;
}

.price-amount {
  font-size: 2rem;
  font-weight: 800;
  color: #2d3748;
}

.price-period {
  font-size: 0.9rem;
  color: #718096;
}

.featured-chip {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
}

.plan-features {
  text-align: left;
  margin-bottom: 1.5rem;
}

.plan-feature {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #4a5568;
}

/* Tables */
.soporte-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.coverage-item {
  display: flex;
  gap: 2px;
}

/* Comparison Section */
.comparison-section {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.comparison-card {
  border-radius: 20px;
}

.comparison-table {
  border-radius: 8px;
  overflow: hidden;
}

.investment-amount {
  font-weight: 700;
  color: #2d3748;
}

/* Contact Section */
.contact-section {
  background: white;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #2d3748;
  position: relative;
  display: inline-block;
}

.section-title::after {
  content: "";
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #0056b3, #17a2b8);
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1.25rem;
  color: #6c757d;
  line-height: 1.6;
  max-width: 700px;
  margin: 1.5rem auto 0;
}

.contact-form-card {
  border-radius: 20px;
  border-top: 6px solid #0056b3;
}

.submit-btn {
  border-radius: 12px !important;
  padding: 0 32px !important;
  font-weight: 600;
  transition: all 0.3s ease;
}

.submit-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 86, 179, 0.25) !important;
}

.contact-info-sidebar {
  padding: 2rem;
  background: #f8f9fa;
  border-radius: 12px;
  height: 100%;
}

.sidebar-title {
  font-weight: 700;
  color: #2d3748;
}

.contact-method {
  display: flex;
  align-items: flex-start;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.method-title {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.method-detail {
  color: #4a5568;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}

.contact-hours {
  padding-top: 1.5rem;
}

.hours-title {
  font-weight: 600;
  color: #2d3748;
}

.hours-detail {
  color: #4a5568;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.emergency-hours {
  color: #dc3545;
  font-weight: 600;
}

/* WhatsApp FAB */
.whatsapp-fab {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%) !important;
  box-shadow: 0 4px 15px rgba(37, 211, 102, 0.3) !important;
  z-index: 1000;
  animation: pulse-whatsapp 2s infinite;
}

@keyframes pulse-whatsapp {
  0% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(37, 211, 102, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
}

/* Emergency Button */
.emergency-btn {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%) !important;
  border-radius: 12px !important;
  font-weight: 700;
  letter-spacing: 0.5px;
  animation: pulse-emergency 1.5s infinite;
}

@keyframes pulse-emergency {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Responsive */
@media (max-width: 960px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .hero-stats {
    flex-direction: column;
    gap: 1.5rem;
  }

  .services-tabs {
    overflow-x: auto;
  }

  .service-tab {
    min-width: 180px;
  }

  .service-detail-title {
    font-size: 1.75rem;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .service-detail-title {
    font-size: 1.5rem;
  }

  .subsection-title {
    font-size: 1.25rem;
  }

  .stat-number {
    font-size: 2rem;
  }
}
</style>
